import {inject} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaderResponse, HttpHeaders, HttpProgressEvent, HttpRequest, HttpResponse, HttpSentEvent, HttpStatusCode, HttpUserEvent } from '@angular/common/http';
import {catchError, exhaustMap, Observable, of, retry, take, tap, skip} from "rxjs";
import {AuthenticationService, EXTERNAL_LOGIN_URL, LOGIN_URL, REFRESH_JWT_TOKEN_URL} from "./authentication.service";
import {ErrorService} from "./error.service";
import {TranslocoService} from "@ngneat/transloco";
import {JwtResponse, TokenRefreshRequest} from "@model";
import {FooterComponent} from "@components";

const SLO: string = 'slo';
const SL: string = 'sl';

export function intercept(httpRequest: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
  let authenticationService: AuthenticationService = inject(AuthenticationService);
  let errorService: ErrorService = inject(ErrorService);
  let translocoService: TranslocoService = inject(TranslocoService);

  return authenticationService.jwtResponse.pipe(take(1), exhaustMap((jwtResponse: JwtResponse | null, _index: number) => {
    if (isImagesUrl(httpRequest) || !jwtResponse?.token) {
      const headers: HttpHeaders = new HttpHeaders({
        'Accept-Language': getLanguage(translocoService),
      });
      const changedHttpRequest: HttpRequest<any> = httpRequest.clone({headers: headers});
      return next(changedHttpRequest);
    } else {
      const headers: HttpHeaders = new HttpHeaders({
        'Authorization': 'Bearer ' + jwtResponse.token,
        'Accept-Language': getLanguage(translocoService),
      });
      const changedHttpRequest: HttpRequest<any> = httpRequest.clone({headers: headers});
      return next(changedHttpRequest);
    }
  })).pipe(tap((httpEvent: HttpSentEvent | HttpHeaderResponse | HttpResponse<any> | HttpProgressEvent | HttpUserEvent<any>) => {
    if (httpEvent instanceof HttpResponse) {
      if (httpEvent.headers.get("X-App-Version")) {
        FooterComponent.backendVersion = httpEvent.headers.get("X-App-Version");
      }
    }
  }), retry({
    count: 1,
    delay: shouldRetry.bind(authenticationService),
  }), catchError((error: any) => errorService.handleError(error)));
}

// Check if it is an images url, and don't enforce authorization on this url
function isImagesUrl(httpRequest: HttpRequest<any>): boolean {
  return httpRequest.url.includes("/images/") || httpRequest.url.includes("/gpx/");
}

/// If there is an 401 error on some non login endpoint then we can try to refresh the JWT token and after that
/// retry the endpoint again if it works,
/// https://blog.adnanhalilovic.com/2022/07/31/retry-error-http-requests-in-angular-without-retrywhen/
function shouldRetry(error: any, _retryCount: number) {
  if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized
    && !error.url?.includes(LOGIN_URL) && !error.url?.includes(EXTERNAL_LOGIN_URL) && !error.url?.includes(REFRESH_JWT_TOKEN_URL)) {

    // @ts-ignore
    if (!this.isRefreshing) { /// we don't want to refresh the token multiple times beacuse this is going to cause a
      // race condition, so this is a flag to check if we are already refreshing the token and if so then in else
      // part of if/else service is waiting to get new value, refreshed jwt token or null
      // @ts-ignore
      this.isRefreshing = true;
      // @ts-ignore
      return this.jwtResponse.pipe(take(1), exhaustMap((jwtResponse: JwtResponse | null, _index: number) => {
        // @ts-ignore
        return this.refreshJwtToken(jwtResponse!, new TokenRefreshRequest(jwtResponse!.refreshToken!, jwtResponse?.username, jwtResponse?.email, jwtResponse?.token));
      }));
    } else {
      // @ts-ignore
      return this.jwtResponse.pipe(  skip(1), take(1), exhaustMap((jwtResponse: JwtResponse | null, _index: number) => {
          if (jwtResponse) {
            return of (jwtResponse);
          }
          throw error;
        }));
    }
  }
  throw error;
}

function getLanguage(translocoService: TranslocoService): string {
  var language: string = translocoService.getActiveLang();
  if (language === SLO) {
    language = SL
  }
  return language;
}
